import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  useTheme,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Collapse,
  Card,
  CardContent,
  Box,
  Divider,
  Chip,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import { themeQuartz } from "ag-grid-community"; //  community theme import
import { formatTwoDecimals, formatOneDecimal, formatPercentNoDecimals, formatValueNoDecimals } from "../utils/formatTwoDecimals";
import "./Card.css";
import { Snackbar, Alert } from "@mui/material";
import { getPlannerPricingError, submitPlannerView } from "../../api/submitPlannerView";
import VersionHistoryView from "../VersionHistory/VersionHistoryView";
import Radio from "@mui/material/Radio";
import { gridTheme } from "../../utils/tableUtils";
import { formControlStyle } from "../../utils/theme";
import { useSnackbar } from "../SnakBar/AppSnackbarProvider";
import { getReasonCodeList } from "../../api/reasonCodeApi";
import { getMaxCoverage, MaxCoverageLimit } from "../../api/maxCoverageApi";

// Props
export type UserActionPopupProps = {
  open: boolean;
  onClose: () => void;
  /** array of selected rows (or a single row object) */
  data: any[] | any | null | undefined; // 🛠️ fixed the typing for clarity
  onSave?: (rows: any[]) => void;
  onSubmitted?: (lastId: string, response: any) => void;
  onSubmitStart?: () => void;
  onSubmitEnd?: () => void;
};

// Helper: convert display header to field name
const toFieldName = (key: string) => {
  if (!key) return key as any;
  const parts = key.split(" ");
  parts[0] = parts[0].charAt(0).toLowerCase() + parts[0].slice(1);
  return parts.join("");
};

const getMaterialId = (item: any) =>
  String(item?.materialNumber ?? item?.["Material Number"] ?? item?.id ?? "").trim();

const getPlantId = (item: any) =>
  String(item?.materialPlant ?? item?.["Material Plant"] ?? "").trim();

const getStateKey = (item: any) => {
  const material = getMaterialId(item);
  const plant = getPlantId(item);
  const inputId = String(item?.inputId ?? item?.["Input Id"] ?? "").trim();
  return [material, plant, inputId].filter(Boolean).join("::") || material;
};

const toDomSafeId = (key: string) => key.replace(/[^a-zA-Z0-9_-]/g, "_");

// Value helpers
export const parseNumberOrText = (value: any) => {
  if (value === null || value === undefined) return "";
  const v = String(value).trim();
  if (v === "-" || v === "") return "";
  const num = Number(v);
  return Number.isFinite(num) ? num : v;
};

const valueParserFromParams = (params) => {
  const v = String(params.newValue ?? "").trim();

  // Reject empty or "-" → return 0
  if (v === "" || v === "-") return 0;

  // Allow only digits or one decimal point
  const numericRegex = /^\d+(\.\d+)?$/;

  if (!numericRegex.test(v)) {
    return params.oldValue; // invalid -> keep previous value
  }

  return Number(v); // valid number -> convert to numeric
};

const valueFormatterDashIfEmpty = (params: any) => {
  const v = params?.value;
  return v === "" || v === null || v === undefined ? "-" : v;
};

// Component
const UserActionPopup: React.FC<UserActionPopupProps> = ({
  open,
  onClose,
  data,
  onSave,
  onSubmitted,
  onSubmitStart,
  onSubmitEnd,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();

  // Correctly normalize incoming data to an array of material rows
  const safeData: any[] = useMemo(
    () => (Array.isArray(data) ? data : data ? [data] : []),
    [data],
  );

  // Per-material state keyed by material id
  type RowChoice = {
    choice: "S4" | "IO" | "New Proposal";
    safetyStock: any;
    coverageProfile: any;
    safetyTime: any;
  };

  type PerItemState = {
    rows: RowChoice[];
    selectedChoice?: RowChoice;
    reasonCode: string;
    comments: string;
    expandedHistory: boolean;
    approverId?: string;
    reasonError?: boolean;
  };

  const [perRowState, setPerRowState] = useState<Record<string, PerItemState>>(
    {},
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // 🔥 ADD THESE
  const [reasonOptions, setReasonOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [reasonLoading, setReasonLoading] = useState(false);
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [maxCoverageByPlant, setMaxCoverageByPlant] = useState<Record<string, MaxCoverageLimit>>({});
  const [limitErrorsByRow, setLimitErrorsByRow] = useState<Record<string, string[]>>({});

  // Utility: check empty string after trimming
  const isEmpty = (v: any) => {
    const s = v === null || v === undefined ? "" : String(v).trim();
    return s === "" || s === "-";
  };

  // 🔁 UPDATED: Validation now auto-fills 0 for New Proposal empties (no blocking)
  const validateItem = (
    item: any,
    s: PerItemState,
  ): { ok: boolean; msg?: string } => {
    // 1) must select a choice (S4, IO, or New Proposal)
    if (!s?.selectedChoice) {
      return {
        ok: false,
        msg: "Please select a User Action (S4, IO, or New Proposal) for this material.",
      };
    }

    // 2) If IO or New Proposal is selected, Reason is mandatory (IO uses auto-reason below)
    if (
      (s.selectedChoice.choice === "S4" ||
        s.selectedChoice.choice === "New Proposal") &&
      isEmpty(s.reasonCode)
    ) {
      return { ok: false, msg: "Please select a Reason before submitting." };
    }

    // 3) For New Proposal: any empty numeric becomes 0 (do not block)
    if (s.selectedChoice.choice === "New Proposal") {
      const patchNum = (v: any) => (isEmpty(v) ? 0 : v);

      setPerRowState((prev) => {
        const id = getStateKey(item);
        const current = prev[id];
        if (!current) return prev;

        const patchedRows = current.rows.map((r) => {
          if (r.choice !== "New Proposal") return r;
          return {
            ...r,
            safetyStock: patchNum(r.safetyStock),
            coverageProfile: patchNum(r.coverageProfile),
            safetyTime: patchNum(r.safetyTime),
          };
        });

        const sc =
          current.selectedChoice?.choice === "New Proposal"
            ? {
              ...current.selectedChoice,
              safetyStock: patchNum(current.selectedChoice.safetyStock),
              coverageProfile: patchNum(
                current.selectedChoice.coverageProfile,
              ),
              safetyTime: patchNum(current.selectedChoice.safetyTime),
            }
            : current.selectedChoice;

        return {
          ...prev,
          [id]: { ...current, rows: patchedRows, selectedChoice: sc },
        };
      });
    }

    return { ok: true };
  };

  // Fetch max-coverage limits for each unique plant when popup opens
  useEffect(() => {
    if (!open || !safeData.length) return;
    let alive = true;

    const uniquePlants = [...new Set(safeData.map((item) => getPlantId(item)).filter(Boolean))];

    Promise.allSettled(
      uniquePlants.map(async (plantId) => {
        const limit = await getMaxCoverage(plantId);
        return { plantId, limit };
      }),
    ).then((results) => {
      if (!alive) return;
      const next: Record<string, MaxCoverageLimit> = {};
      results.forEach((r) => {
        if (r.status === "fulfilled") next[r.value.plantId] = r.value.limit;
      });
      setMaxCoverageByPlant(next);
      setLimitErrorsByRow({});
    });

    return () => { alive = false; };
  }, [open, safeData]);

  // Recompute limit errors whenever plant limits or per-row state changes
  useEffect(() => {
    const next: Record<string, string[]> = {};
    safeData.forEach((item) => {
      const stateKey = getStateKey(item);
      const s = stateKey ? perRowState[stateKey] : undefined;
      if (!s?.selectedChoice) return;

      const plantId = getPlantId(item);
      const plantLimit = maxCoverageByPlant[plantId];
      if (!plantLimit) return;

      const messages: string[] = [];
      const safetyTime = toSafeNumber(s.selectedChoice.safetyTime);
      const coverageProfile = toSafeNumber(s.selectedChoice.coverageProfile);

      if (safetyTime > plantLimit.safetyTime)
        messages.push(`Safety Time ${safetyTime} exceeds the plant limit of ${plantLimit.safetyTime}`);
      if (coverageProfile > plantLimit.coverageProfile)
        messages.push(`Coverage Profile ${coverageProfile} exceeds the plant limit of ${plantLimit.coverageProfile}`);

      if (messages.length) next[stateKey] = messages;
    });
    setLimitErrorsByRow(next);
  }, [perRowState, maxCoverageByPlant, safeData]);

  // 🔥 ADD THIS useEffect to fetch + normalize reason list
  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      try {
        setReasonLoading(true);
        const apiList = await getReasonCodeList();
        // EXPECTS: [{reasonCode:"5", reasonDescription:"Maintain…" } ...]
        const mapped = (apiList ?? []).map((r: any) => ({
          value: Number(r.reasonCode), // <-- store reasonCode
          label: String(r.reasonDescription), // <-- display description
        }));
        if (alive) setReasonOptions(mapped);
      } catch (err) {
        if (alive) setReasonError("Failed to load reasons");
      } finally {
        if (alive) setReasonLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [open]);

  // Build initial per-item rows from incoming data
  useEffect(() => {
    const init: Record<string, PerItemState> = {};
    safeData.forEach((item: any) => {
      const id = getStateKey(item);
      if (!id) return;

      init[id] = {
        rows: [
          {
            choice: "S4",
            safetyStock: item?.s4SafetyStockQtyRaw ?? item?.["s4SafetyStock(Qty)"] ?? "-",
            safetyTime: item?.s4SafetyTimeRaw ?? item?.["s4SafetyTime(weeks)"] ?? "-",
            coverageProfile: item?.s4CoverageProfileRaw ?? item?.["s4Coverageprofile(weeks)"] ?? "-",
          },
          {
            choice: "IO",
            safetyStock: item?.ioSafetyStockQtyRaw ?? item?.["ioSafetyStockQty"] ?? "-",
            safetyTime: item?.ioSafetyTimeRaw ?? item?.["iOSafetyTime(Weeks)(Rounded)"] ?? "-",
            coverageProfile: item?.ioCoverageProfileRaw ?? item?.["iOCoverageprofile(weeks)"] ?? "-",
          },
          {
            choice: "New Proposal",
            safetyStock: item?.["Safety Stock (Qty) - End User Proposal"] ?? "-",
            safetyTime: item?.["Safety Time (wk) - End User Proposal"] ?? "-",
            coverageProfile: item?.["Coverage Profile (wk) - End User Proposal"] ?? "-",
          },
        ],
        approverId: item?.approverId,
        selectedChoice: {
          choice: "S4",
          safetyStock: item?.s4SafetyStockQtyRaw ?? "-",
          safetyTime: item?.s4SafetyTimeRaw ?? "-",
          coverageProfile: item?.s4CoverageProfileRaw ?? "-",
        },
        reasonCode: "",
        comments: "",
        expandedHistory: true,
      };
    });
    setPerRowState(init);
  }, [safeData]);

  // Handlers
  const setSelectedRowForId = (id: string, selectedRow: RowChoice) => {
    setPerRowState((prev) => {
      const current = prev[id];
      if (!current) return prev;
      return { ...prev, [id]: { ...current, selectedChoice: selectedRow } };
    });
  };

  const handleCellValueChanged = (id: string, params: any) => {
    const { data: rowData, colDef, newValue } = params;

    // Auto-select "New Proposal" row when user edits any of its cells
    if (rowData.choice === "New Proposal") {
      params.api.getRowNode("New Proposal")?.setSelected(true, true);
    }

    setPerRowState((prev) => {
      const current = prev[id];
      if (!current) return prev;

      const updatedRows = current.rows.map((r) =>
        r.choice === rowData.choice
          ? { ...r, [String(colDef.field)]: newValue }
          : r,
      );

      const updatedSelected =
        rowData.choice === "New Proposal"
          ? { ...rowData, [String(colDef.field)]: newValue }
          : current.selectedChoice?.choice === rowData.choice
            ? { ...rowData, [String(colDef.field)]: newValue }
            : current.selectedChoice;

      return {
        ...prev,
        [id]: {
          ...current,
          rows: updatedRows,
          selectedChoice: updatedSelected,
        },
      };
    });
  };

  const handleSelectionChanged = (id: string, event: any) => {
    const selectedRows = event.api.getSelectedRows(); // Get all selected rows
    const selectedRow = selectedRows[0]; // Since it's single selection, take the first row
    if (!selectedRow) return; // If there's no row selected, exit early

    // Proceed with updating the state only if the selected row changes
    setPerRowState((prevState) => {
      const currentState = prevState[id];
      if (!currentState) return prevState; // No existing state for this ID

      // If the row selection has changed, update selectedChoice
      if (currentState.selectedChoice?.choice !== selectedRow?.choice) {
        return {
          ...prevState,
          [id]: {
            ...currentState,
            selectedChoice: selectedRow, // Set the selected row as selectedChoice
          },
        };
      }
      // If the row was already selected, return prevState without any change
      return prevState;
    });
  };

  const handleReasonChange = (id: string, value: string) =>
    setPerRowState((prev) => ({
      ...prev,
      [id]: { ...prev[id], reasonCode: value, reasonError: !value },
    }));

  const handleCommentsChange = (id: string, value: string) =>
    setPerRowState((prev) => ({
      ...prev,
      [id]: { ...prev[id], comments: value },
    }));

  const toggleHistory = (id: string) =>
    setPerRowState((prev) => ({
      ...prev,
      [id]: { ...prev[id], expandedHistory: !prev[id].expandedHistory },
    }));

  const toSafeString = (v: any) => {
    if (v === null || v === undefined || Number.isNaN(v)) return "";
    return String(v).trim();
  };

  const toSafeNumber = (v: any) => {
    if (v === null || v === undefined || v === "") return 0;
    if (typeof v === "number") return Number.isFinite(v) ? v : 0;
    const cleaned = String(v)
      .trim()
      .replace(/,/g, "")
      .replace(/[^\d.-]/g, "");
    if (!cleaned || cleaned === "-" || cleaned === "." || cleaned === "-.") return 0;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const getReasonCode = (selectedChoice: any, stateForItem: PerItemState) => {
    if (selectedChoice === "IO") {
      return "IO selected";
    } else {
      return (
        reasonOptions?.find((r) => r.value === Number(stateForItem?.reasonCode))
          ?.label ?? ""
      );
    }
  };

  const buildSubmitPayload = (
    item: any,
    stateForItem: PerItemState,
    submitStatus: boolean,
  ) => {
    const idSafe =
      String(
        item?.materialNumber ??
        item?.["Material Number"] ??
        item?.id ??
        item?.Id ??
        item?.ID ??
        "",
      ).trim() || `tmp-${Date.now()}`;

    const choice = stateForItem?.selectedChoice;
    const chosen: RowChoice = choice ?? {
      choice: "New Proposal",
      safetyStock: "",
      coverageProfile: "",
      safetyTime: "",
    };

    let submitSafetyStock: string;
    let submitCoverage: string;
    let submitSafetyTime: string;

    if (chosen.choice === "S4") {
      submitSafetyStock = item?.s4SafetyStockQtyRaw ?? item?.["s4SafetyStock(Qty)"];
      submitCoverage = item?.s4CoverageProfileRaw ?? item?.["s4Coverageprofile(weeks)"];
      submitSafetyTime = item?.s4SafetyTimeRaw ?? item?.["s4SafetyTime(weeks)"];
    } else if (chosen.choice === "IO") {
      submitSafetyStock = "0";
      submitCoverage = item?.ioCoverageProfileRaw ?? item?.["iOCoverageprofile(weeks)"];
      submitSafetyTime = item?.ioSafetyTimeRaw ?? item?.["iOSafetyTime(Weeks)(Rounded)"];
    } else {
      // Apply same rounding as display: safetyStock → 0 decimals, coverage/time → 1 decimal
      submitSafetyStock = String(Math.round(toSafeNumber(chosen.safetyStock)));
      submitCoverage = String(parseFloat(toSafeNumber(chosen.coverageProfile).toFixed(1)));
      submitSafetyTime = String(parseFloat(toSafeNumber(chosen.safetyTime).toFixed(1)));
    }

    const updatedData: any = {
      materialNumber:
        item?.materialNumber ?? item?.["Material Number"] ?? idSafe,
      materialPlant: item?.materialPlant ?? item?.["Material Plant"] ?? 5040, // default plant ID
      region: item?.region, // default region
      userSelectedChoice: chosen.choice,
      userComments: stateForItem?.comments ?? "",
      userReasonCode: getReasonCode(chosen.choice, stateForItem),
      userSafetyStockQty: toSafeNumber(submitSafetyStock),
      userCoverageProfile: toSafeNumber(submitCoverage),
      userSafetyTime: toSafeNumber(submitSafetyTime),
      userConsolidatedSafetyStock: toSafeNumber(item?.["s4ConsolidatedSS(weeks)"]), // can adjust this depending on your logic
      varWkUserVsS4: toSafeNumber(item?.["varWkUserVsS4"]), // Adjust logic if needed
      varPctUserVsS4: toSafeNumber(item?.["varPctUserVsS4"]), // Adjust logic if needed
      approverId: item?.approverId,
      userId: localStorage.getItem("userId") || sessionStorage.getItem("userId") || "",
      ioExtractionDate: item.extractionDate
    };
    return updatedData;
  };

  const handleSubmitAll = async () => {
    if (isSubmitting) return; // prevent double click

    // Run validation BEFORE closing — so the user can fix errors inline
    for (const item of safeData) {
      const stateKey = getStateKey(item);
      if (!stateKey) continue;
      const s = perRowState[stateKey];
      if (!s) continue;
      const v = validateItem(item, s);
      if (!v.ok) {
        // Mark all rows with empty reason so borders appear immediately
        setPerRowState((prev) => {
          const next = { ...prev };
          safeData.forEach((it) => {
            const key = getStateKey(it);
            if (!key || !next[key]) return;
            const st = next[key];
            if (st.selectedChoice?.choice !== "IO" && isEmpty(st.reasonCode ?? "")) {
              next[key] = { ...st, reasonError: true };
            }
          });
          return next;
        });
        enqueueSnackbar({
          message: v.msg!,
          severity: "error",
          autoHideDuration: 2500,
        });
        return;
      }
    }

    // Block submit if any row exceeds plant coverage limits
    if (Object.keys(limitErrorsByRow).length > 0) {
      enqueueSnackbar({
        message: "Please fix the coverage limit violations before submitting.",
        severity: "error",
        autoHideDuration: 3000,
      });
      return;
    }

    setIsSubmitting(true); // lock submit button
    onSubmitStart?.();     // notify parent — disable other Take Action buttons
    onClose();             // close only after validation passes

    const inProgressId = enqueueSnackbar({
      message: "Submitting proposals for materials",
      severity: "warning",
      persist: true,
      autoHideDuration: null,
    });

    const submittedPayloads: any[] = [];
    let lastId = "";
    let lastResponse: any;
    try {
      for (const item of safeData) {
        const stateKey = getStateKey(item);
        if (!stateKey) continue;

        const s = perRowState[stateKey];
        if (!s) continue;

        const payload = buildSubmitPayload(item, s, true);
        submittedPayloads.push(payload);
        lastId = getMaterialId(item);

        // API call
        lastResponse = await submitPlannerView([payload]);
      }

      sessionStorage.setItem(
        "approverRows",
        JSON.stringify(submittedPayloads),
      );
      onSubmitted?.(String(lastId), lastResponse);
    } catch (error) {
      enqueueSnackbar({
        message: getPlannerPricingError(error) ?? "Failed to submit data.",
        severity: "error",
        autoHideDuration: 2500,
      });
    } finally {
      closeSnackbar(inProgressId);
      setIsSubmitting(false);
      onSubmitEnd?.();     // notify parent — re-enable Take Action buttons
    }
  };

  const RadioCellRenderer = (props: any) => {
    const onChange = () => {
      props.node.setSelected(true);
    };
    return (
      <Radio
        checked={props.node.isSelected()}
        onChange={onChange}
        value={props.data.choice}
        size="small" //  small size
        sx={{
          color: "#000", //  black (unchecked)
          "&.Mui-checked": {
            color: "#000", //  black (checked)
          },
        }}
      />
    );
  };

  // Popup sub-grids
  const actionColumns: ColDef[] = [
    {
      headerName: "",
      cellRenderer: RadioCellRenderer,
      width: 30,
      maxWidth: 30,
      cellStyle: {
        textAlign: "center",
        padding: "0 8px",
        pointerEvents: "auto",
      },
      sortable: false,
      filter: false,
      resizable: false,
    },
    {
      field: "choice",
      headerName: "Choice",
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
    },
    {
      field: "safetyStock",
      headerName: "Safety Stock (qty)",
      editable: (p) => p.data?.choice === "New Proposal",
      cellEditor: "agTextCellEditor",
      valueParser: valueParserFromParams,
      valueFormatter: ({ value }) => formatValueNoDecimals(value),

      cellClassRules: {
        "ag-cell-invalid": (p) =>
          p.data?.choice === "New Proposal" &&
          p.value !== "-" &&
          isEmpty(p.value),
      },
      cellStyle: (p) => {
        const base: any = { display: "flex", alignItems: "center", justifyContent: "center" };
        if (p.data?.choice === "New Proposal" && p.value !== "-" && (isEmpty(p.value) || p.value === 0))
          return { ...base, border: "1.5px solid #d32f2f", backgroundColor: "#fff5f5" };
        return base;
      },
    },
    {
      field: "coverageProfile",
      headerName: "Coverage Profile (weeks)",
      editable: (p) => p.data?.choice === "New Proposal",
      cellEditor: "agTextCellEditor",
      valueParser: valueParserFromParams,
      valueFormatter: ({ value }) => formatOneDecimal(value),
      cellClassRules: {
        "ag-cell-invalid": (p) =>
          p.data?.choice === "New Proposal" &&
          p.value !== "-" &&
          isEmpty(p.value),
      },
      cellStyle: (p) => {
        const base: any = { display: "flex", alignItems: "center", justifyContent: "center" };
        if (p.data?.choice === "New Proposal" && p.value !== "-" && (isEmpty(p.value) || p.value === 0))
          return { ...base, border: "1.5px solid #d32f2f", backgroundColor: "#fff5f5" };
        return base;
      },
    },
    {
      field: "safetyTime",
      headerName: "Safety Time (weeks)",
      editable: (p) => p.data?.choice === "New Proposal",
      cellEditor: "agTextCellEditor",
      valueParser: valueParserFromParams,
      valueFormatter: ({ value }) => formatOneDecimal(value),

      cellClassRules: {
        "ag-cell-invalid": (p) =>
          p.data?.choice === "New Proposal" &&
          p.value !== "-" &&
          isEmpty(p.value),
      },
      cellStyle: (p) => {
        const base: any = { display: "flex", alignItems: "center", justifyContent: "center" };
        if (p.data?.choice === "New Proposal" && p.value !== "-" && (isEmpty(p.value) || p.value === 0))
          return { ...base, border: "1.5px solid #d32f2f", backgroundColor: "#fff5f5" };
        return base;
      },
    },
  ];

  function getDynamicVariationRows(item: any, selectedChoice: any) {
    if (!selectedChoice) return [];
    const num = (v: any) => (Number.isFinite(Number(v)) ? Number(v) : 0);

    // ===============================
    // Base values
    // ===============================
    const weeklyDemand = num(item?.["weeklyDemandQty"]);
    const s4Consolidated = num(item?.s4ConsolidatedSafetyStockRaw ?? item?.["s4ConsolidatedSS(weeks)"]);
    const ioConsolidated = num(item?.ioConsolidatedSafetyStockRaw ?? item?.["iOConsolidatedSS(weeks)"]);

    // ===============================
    // Pick values based on selection
    // ===============================
    let safetyStockQty = 0;
    let coverageProfile = 0;
    let safetyTime = 0;

    switch (selectedChoice.choice) {
      case "S4":
        safetyStockQty = num(item?.s4SafetyStockQtyRaw ?? item?.["s4SafetyStock(Qty)"]);
        coverageProfile = num(item?.s4CoverageProfileRaw ?? item?.["s4Coverageprofile(weeks)"]);
        safetyTime = num(item?.s4SafetyTimeRaw ?? item?.["s4SafetyTime(weeks)"]);
        break;
      case "IO":
        safetyStockQty = num(item?.ioSafetyStockQtyRaw ?? item?.["ioSafetyStockQty"]);
        coverageProfile = num(item?.ioCoverageProfileRaw ?? item?.["iOCoverageprofile(weeks)"]);
        safetyTime = num(item?.ioSafetyTimeRaw ?? item?.["iOSafetyTime(Weeks)(Rounded)"]);
        break;
      case "New Proposal":
        safetyStockQty = num(selectedChoice.safetyStock);
        coverageProfile = num(selectedChoice.coverageProfile);
        safetyTime = num(selectedChoice.safetyTime);
        break;
    }

    // ===============================
    // Consolidated SS (wk)
    // ===============================
    const consolidatedSelected =
      weeklyDemand === 0
        ? coverageProfile + safetyTime
        : safetyStockQty / weeklyDemand + coverageProfile + safetyTime;

    // ===============================
    // Variations
    // ===============================
    const varWkVsIO = consolidatedSelected - ioConsolidated;
    const varWkVsS4 = consolidatedSelected - s4Consolidated;

    const varPctVsIO =
      ioConsolidated === 0
        ? "N/A"
        : Math.round((varWkVsIO / ioConsolidated) * 100) + "%";
    const varPctVsS4 =
      s4Consolidated === 0
        ? "N/A"
        : Math.round((varWkVsS4 / s4Consolidated) * 100) + "%";

    return [
      {
        metric: `End User vs IO`,
        week: varWkVsIO.toFixed(1),
        percentage: varPctVsIO,
      },
      {
        metric: `End User vs S4`,
        week: varWkVsS4.toFixed(1),
        percentage: varPctVsS4,
      },
    ];
  }

  // Variation columns (read-only)
  const variationColumns: ColDef[] = [
    {
      field: "metric",
      headerName: "Metric",
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      valueFormatter: ({ value }) => formatTwoDecimals(value),
    },
    {
      field: "week",
      headerName: "Week",
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      valueFormatter: ({ value }) => formatOneDecimal(value),
    },
    {
      field: "percentage",
      headerName: "Percentage",
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      valueFormatter: ({ value }) => formatPercentNoDecimals(value),
    },
  ];
  const isMissing = (v: any) =>
    v === "" || v === "-" || v === null || v === undefined;

  const isRowIncomplete = (itemId: string) => {
    const s = perRowState[itemId];
    if (!s) return true;

    const choice = s.selectedChoice?.choice;

    if (choice === "S4") {
      return isMissing(s.reasonCode);
    }

    if (choice === "New Proposal") {
      const sc = s.selectedChoice;

      const allFilled =
        !isMissing(sc.safetyStock) &&
        !isMissing(sc.coverageProfile) &&
        !isMissing(sc.safetyTime) &&
        !isMissing(s.reasonCode);

      return !allFilled;
    }

    return false;
  };

  const getMissingFieldsMessage = (itemId: string) => {
    const s = perRowState[itemId];
    if (!s)
      return "Please complete the required fields below before submitting.";

    const choice = s.selectedChoice?.choice;
    const missing: string[] = [];

    if (choice === "S4") {
      if (isMissing(s.reasonCode)) missing.push("Reason Code");
    }

    if (choice === "New Proposal") {
      if (isMissing(s.selectedChoice?.safetyStock))
        missing.push("Safety Stock");
      if (isMissing(s.selectedChoice?.coverageProfile))
        missing.push("Coverage Profile");
      if (isMissing(s.selectedChoice?.safetyTime)) missing.push("Safety Time");
      if (isMissing(s.reasonCode)) missing.push("Reason Code");
    }

    return missing.length
      ? `Please complete the required fields before submitting:\n- ${missing.join(", ")}`
      : "";
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      {/* Header */}
      <Stack
        direction="row"
        spacing={2}
        sx={{ alignItems: "center", justifyContent: "space-between", p: 1 }}
      >
        <Typography variant="h6" textAlign="center" flex={1}>
          Viewing details for {safeData.length} selected materials
        </Typography>
      </Stack>

      {/* Per-material sections */}
      {safeData.map((item: any, index: number) => {
        const stateKey = getStateKey(item);
        const domId = toDomSafeId(stateKey);
        const s = stateKey ? perRowState[stateKey] : undefined;

        const variationRows = getDynamicVariationRows(item, s?.selectedChoice);

        return (
          <DialogContent
            key={stateKey || index}
            sx={{ pt: index === 0 ? 2 : 0, pb: 2 }}
          >
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              Material #{item?.materialNumber ?? getMaterialId(item) ?? "-"} —{" "}
              {item?.materialDescription ?? "Details"}
            </Typography>

            <Stack direction="row" spacing={4} sx={{ width: "100%" }}>
              {/* Left: Variation + History */}
              <Stack
                sx={{
                  flex: 0.9,
                  minWidth: 380,
                  borderRight: "1px solid #ddd",
                  pr: 2,
                }}
              >
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Variation Metrics
                </Typography>

                <Paper sx={{ height: "160px", width: "100%" }}>
                  <div
                    className="ag-theme-quartz"
                    style={{ height: "100%", width: "100%" }}
                  >
                    <AgGridReact
                      columnDefs={variationColumns}
                      rowData={variationRows}
                      pagination={false}
                      autoSizeStrategy={{ type: "fitGridWidth" }}
                      theme={gridTheme}
                      gridOptions={{
                        sideBar: { hiddenByDefault: true },
                        suppressHorizontalScroll: false,
                      }}
                      rowHeight={40}
                      defaultColDef={{
                        valueFormatter: (p: any) => {
                          // 1) Skip for checkbox/header-checkbox columns
                          const isSelectionCol =
                            p.colDef.checkboxSelection ||
                            p.colDef.headerCheckboxSelection ||
                            !p.colDef.field ||
                            // columns without a field, e.g., selection col
                            p.column.getColId() === "ag-Grid-AutoColumn";
                          if (isSelectionCol) return ""; // let the checkbox render cleanly

                          // 2) Your normal hyphen-for-empty logic
                          const v = p.value;
                          if (
                            v === null ||
                            v === undefined ||
                            (typeof v === "string" && v.trim() === "") ||
                            (typeof v === "number" && Number.isNaN(v))
                          ) {
                            return "-";
                          }
                          return v;
                        },
                      }}
                    />
                  </div>
                </Paper>

                <VersionHistoryView
                  id={getMaterialId(item)}
                  s={{ ...s, materialNumber: item?.materialNumber, materialPlant: item.materialPlant }}
                  toggleHistory={toggleHistory}
                />
              </Stack>

              {/* Right: User Actions */}
              <Stack sx={{ flex: 1.1, minWidth: 520 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  User Actions
                </Typography>

                <Paper sx={{ height: "200px", width: "100%" }}>
                  <div
                    data-testid="ag-grid-container"
                    className="ag-theme-quartz"
                    style={{ height: "100%", width: "100%" }}
                  >
                    <AgGridReact
                      columnDefs={actionColumns}
                      rowData={s?.rows ?? []}
                      pagination={false}
                      autoSizeStrategy={{ type: "fitGridWidth" }}
                      theme={gridTheme}
                      gridOptions={{
                        sideBar: { hiddenByDefault: true },
                        suppressHorizontalScroll: false,
                      }}
                      rowHeight={40}
                      defaultColDef={{
                        valueFormatter: (p: any) => {
                          // 1) Skip for checkbox/header-checkbox columns
                          const isSelectionCol =
                            p.colDef.checkboxSelection ||
                            p.colDef.headerCheckboxSelection ||
                            !p.colDef.field ||
                            // columns without a field, e.g., selection col
                            p.column.getColId() === "ag-Grid-AutoColumn";
                          if (isSelectionCol) return ""; // let the checkbox render cleanly

                          // 2) Your normal hyphen-for-empty logic
                          const v = p.value;
                          if (
                            v === null ||
                            v === undefined ||
                            (typeof v === "string" && v.trim() === "") ||
                            (typeof v === "number" && Number.isNaN(v))
                          ) {
                            return "-";
                          }
                          return v;
                        },
                      }}
                      rowSelection="single"
                      getRowId={(params) => params.data.choice}
                      suppressRowClickSelection={true} //  only the checkbox selects
                      onGridReady={(params) => {
                        params.api.forEachNode((node) => {
                          if (node.data?.choice === "S4") {
                            node.setSelected(true);
                          }
                        });
                      }}
                      onSelectionChanged={(event) =>
                        stateKey && handleSelectionChanged(stateKey, event)
                      } // Use onSelectionChanged here
                      onCellValueChanged={(params) =>
                        stateKey && handleCellValueChanged(stateKey, params)
                      }
                    />
                  </div>
                </Paper>
                <div style={{ marginTop: "12px" }}>
                  {limitErrorsByRow[stateKey]?.length > 0 && (
                    <Box sx={{ color: "error.main", fontSize: 12, mb: 1 }}>
                      {limitErrorsByRow[stateKey].map((msg, i) => (
                        <div key={i}>⚠️ {msg}</div>
                      ))}
                    </Box>
                  )}
                  <FormControl
                    fullWidth
                    required
                    error={!!(s?.reasonError && isEmpty(s?.reasonCode ?? ""))}
                    sx={{ mb: 2, ...formControlStyle }}
                  >
                    <InputLabel
                      sx={{
                        display:
                          s?.selectedChoice?.choice === "IO" ? "none" : "block",
                      }}
                      id={`reason-label-${domId}`}
                    >
                      Reason
                    </InputLabel>

                    {s?.selectedChoice?.choice === "IO" ? null : (
                      <>
                        <Select
                          labelId={`reason-label-${domId}`}
                          id={`reason-${domId}`}
                          label="Reason"
                          value={s?.reasonCode ?? ""}
                          error={!!(s?.reasonError && isEmpty(s?.reasonCode ?? ""))}
                          onChange={(e) =>
                            stateKey &&
                            handleReasonChange(stateKey, String(e.target.value))
                          }
                          displayEmpty
                        >
                          {reasonLoading && (
                            <MenuItem value="" disabled>
                              Loading reasons…
                            </MenuItem>
                          )}
                          {reasonOptions.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                          {/* optional error */}
                          {reasonError && (
                            <MenuItem disabled>
                              <em>{reasonError}</em>
                            </MenuItem>
                          )}
                        </Select>
                        {s?.reasonError && isEmpty(s?.reasonCode ?? "") && (
                          <Box sx={{ color: "error.main", fontSize: 12, mt: 0.5 }}>
                            Reason is required
                          </Box>
                        )}
                      </>
                    )}
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Add your Comments here"
                    variant="outlined"
                    value={s?.comments ?? ""}
                    onChange={(e) =>
                      stateKey && handleCommentsChange(stateKey, e.target.value)
                    }
                    sx={{ mb: 2, ...formControlStyle }}
                  />
                  {s?.comments.length>500 &&
                  <Box sx={{ color: "error.main", fontSize: 12, mt: -2 }}>
                    Comments cannot exceed 500 characters.
                  </Box>}
                </div>
              </Stack>
            </Stack>
          </DialogContent>
        );
      })}

      {/* Actions */}
      <DialogActions style={{ position: "sticky", bottom: 0, zIndex: 1 }}>
        <Tooltip
          placement="top"
          sx={{ color: "red" }}
          arrow
          title={
            Object.keys(limitErrorsByRow).length > 0
              ? "Fix coverage limit violations before submitting."
              : safeData.some((row) => isRowIncomplete(getStateKey(row)))
                ? getMissingFieldsMessage(getStateKey(safeData[0]))
                : ""
          }
        >
          <span>
            <Button
              disabled={
                isSubmitting ||
                Object.keys(limitErrorsByRow).length > 0 ||
                safeData.some((row) =>
                  isRowIncomplete(getStateKey(row)),
                ) ||
                safeData.some(
                  (row) => (perRowState[getStateKey(row)]?.comments.length ?? 0) > 500,
                )
              }
              onClick={handleSubmitAll}
              variant="outlined"
              sx={{
                borderRadius: "50px",
                border: "0.74px solid green",
                color: "#000",
                "&:hover": { backgroundColor: "green", color: "#fff" },
                "&.Mui-disabled": {
                  backgroundColor: "#e0e0e0",
                  borderColor: "#9e9e9e",
                  color: "#555",
                  opacity: 1,
                  cursor: "not-allowed",
                },
              }}
            >
              {isSubmitting ? (
                <span>
                  Submitting{" "}
                  <CircularProgress size={20} sx={{ color: "#000000" }} />{" "}
                </span>
              ) : (
                "Submit"
              )}
            </Button>
          </span>
        </Tooltip>

        <Button
          onClick={onClose}
          color="error"
          variant="outlined"
          sx={{
            borderRadius: "50px",
            backgroundColor: "#000",
            color: "#fff",
            "&:hover": { backgroundColor: "red", color: "#fff" },
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserActionPopup;
